import type { EffectRegion } from "@client/types/vision";
import { getRegionBounds, isInRegion } from "./regionMask";

/**
 * Apply brightness adjustment to image data
 * @param imageData - ImageData to modify
 * @param brightness - Brightness multiplier (1 = no change)
 * @returns Modified ImageData
 */
export function applyBrightnessToImageData(
	imageData: ImageData,
	brightness: number,
	region?: EffectRegion | null,
): ImageData {
	const data = imageData.data;

	if (region) {
		const width = imageData.width;
		const bounds = getRegionBounds(region);
		const rx1 = Math.max(0, Math.floor(bounds.rx1));
		const ry1 = Math.max(0, Math.floor(bounds.ry1));
		const rx2 = Math.min(imageData.width, Math.ceil(bounds.rx2));
		const ry2 = Math.min(imageData.height, Math.ceil(bounds.ry2));
		const isPolygon = region.type === "polygon";

		for (let y = ry1; y < ry2; y++) {
			for (let x = rx1; x < rx2; x++) {
				if (isPolygon && !isInRegion(x, y, region)) continue;
				const i = (y * width + x) * 4;
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];
				if (r !== undefined && g !== undefined && b !== undefined) {
					data[i] = Math.min(255, Math.max(0, r * brightness));
					data[i + 1] = Math.min(255, Math.max(0, g * brightness));
					data[i + 2] = Math.min(255, Math.max(0, b * brightness));
				}
			}
		}
		return imageData;
	}

	for (let i = 0; i < data.length; i += 4) {
		const r = data[i];
		const g = data[i + 1];
		const b = data[i + 2];
		if (r !== undefined && g !== undefined && b !== undefined) {
			data[i] = Math.min(255, Math.max(0, r * brightness)); // Red
			data[i + 1] = Math.min(255, Math.max(0, g * brightness)); // Green
			data[i + 2] = Math.min(255, Math.max(0, b * brightness)); // Blue
			// Alpha channel remains unchanged
		}
	}
	return imageData;
}
